import { useEffect, useState } from 'react';
import { useCustomer } from '../../../lib/customer';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { useCurrency } from '../../../lib/currency';
import { PortalContainer } from '../PortalShell';

type Quote = {
  id: string;
  quote_number: string;
  kind: 'to_nuled' | 'to_client';
  status: string;
  total: number;
  subtotal: number | null;
  currency: string;
  project_title: string | null;
  project_reference: string | null;
  client_name: string | null;
  markup_pct: number | null;
  valid_until: string | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  notes: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  created_at: string;
};

type QuoteItem = {
  id: string;
  sku: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_pct: number;
  line_total: number;
};

export function PortalCotizaciones() {
  const { customer } = useCustomer();
  const [view, setView] = useState<'to_nuled' | 'to_client'>('to_nuled');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [openQuote, setOpenQuote] = useState<Quote | null>(null);

  useEffect(() => {
    if (!customer) return;
    setLoading(true);
    supabase
      .from('quotes')
      .select('id, quote_number, kind, status, total, subtotal, currency, project_title, project_reference, client_name, markup_pct, valid_until, payment_terms, delivery_terms, notes, sent_at, viewed_at, created_at')
      .eq('customer_id', customer.id)
      .eq('kind', view)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setQuotes((data as Quote[]) ?? []);
        setLoading(false);
      });
  }, [customer, view]);

  if (!customer) return null;

  const handleOpen = async (q: Quote) => {
    setOpenQuote(q);
    // Si está enviada y no había sido vista, marcar como vista
    if (q.status === 'sent' && !q.viewed_at) {
      const ts = new Date().toISOString();
      await supabase.from('quotes').update({ viewed_at: ts }).eq('id', q.id);
      setQuotes(prev => prev.map(x => x.id === q.id ? { ...x, viewed_at: ts } : x));
    }
  };

  const pendientes = quotes.filter(q => q.status === 'sent').length;

  return (
    <PortalContainer title="Cotizaciones" eyebrow="Mayoreo y a tu cliente">
      <div style={{
        display: 'inline-flex',
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        marginBottom: 20,
      }}>
        <Toggle active={view === 'to_nuled'} onClick={() => setView('to_nuled')}>
          📥 Cotizaciones a NULED
          {pendientes > 0 && view !== 'to_nuled' && (
            <span style={{
              marginLeft: 8,
              background: theme.warn, color: theme.bg,
              padding: '1px 7px', borderRadius: 999,
              fontSize: 10, fontWeight: 700,
            }}>{pendientes}</span>
          )}
        </Toggle>
        <Toggle active={view === 'to_client'} onClick={() => setView('to_client')}>
          📤 Cotizaciones a mi cliente
        </Toggle>
      </div>

      {view === 'to_nuled' && (
        <Banner color={theme.accent}>
          Aquí ves las cotizaciones que NULED te ha enviado o que tú solicitaste, al precio de tu tier{' '}
          <strong>{customer.tier_name ?? '—'}</strong> con descuento{' '}
          <strong>{customer.tier_discount_pct ?? 0}%</strong> sobre PVP.
        </Banner>
      )}
      {view === 'to_client' && (
        <Banner color={theme.brandTag.lutron}>
          Cotizaciones que tú generaste para tus clientes finales, con tu propio markup
          (default {customer.default_markup_pct ?? 35}%). Tu cliente nunca ve el precio mayoreo.
        </Banner>
      )}

      {loading ? (
        <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>
      ) : quotes.length === 0 ? (
        <div style={{
          padding: 40,
          background: theme.bg,
          border: `1px dashed ${theme.border}`,
          textAlign: 'center',
          color: theme.textMuted,
        }}>
          Aún no tienes cotizaciones {view === 'to_nuled' ? 'a NULED' : 'a tu cliente'}.
          {view === 'to_nuled' && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Cuando NULED te envíe una, aparecerá aquí y te llegará por WhatsApp/correo.
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bgSoft }}>
                <Th>Folio</Th>
                <Th>{view === 'to_nuled' ? 'Proyecto / Notas' : 'Cliente'}</Th>
                <Th style={{ textAlign: 'right' }}>Total</Th>
                {view === 'to_client' && <Th>Markup</Th>}
                <Th>Status</Th>
                <Th>Fecha</Th>
                <Th style={{ textAlign: 'center' }}>PDF</Th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => (
                <tr
                  key={q.id}
                  onClick={() => handleOpen(q)}
                  style={{
                    borderBottom: `1px solid ${theme.border}`,
                    cursor: 'pointer',
                    background: q.status === 'sent' ? 'rgba(245,158,11,0.04)' : theme.bg,
                  }}
                >
                  <Td>
                    <span style={{ fontFamily: theme.font.mono, color: theme.brand, fontWeight: 600 }}>
                      {q.quote_number}
                    </span>
                    {q.status === 'sent' && (
                      <div style={{ fontSize: 9, color: theme.warn, fontWeight: 700, marginTop: 2 }}>
                        ● NUEVA · sin abrir
                      </div>
                    )}
                  </Td>
                  <Td>{view === 'to_nuled' ? (q.project_title ?? q.notes?.slice(0, 60) ?? '—') : (q.client_name ?? '—')}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>
                    ${Number(q.total).toLocaleString('en-US', { maximumFractionDigits: 2 })} {q.currency}
                  </Td>
                  {view === 'to_client' && <Td>{q.markup_pct != null ? `+${q.markup_pct}%` : '—'}</Td>}
                  <Td><StatusTag status={q.status} /></Td>
                  <Td style={{ color: theme.textMuted, fontSize: 12 }}>
                    {new Date(q.created_at).toLocaleDateString('es-MX')}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <div
                      style={{ display: 'inline-flex', gap: 4 }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <a
                        href={`/cotizacion/${q.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="PDF para cliente final (solo PVP)"
                        style={{
                          background: theme.accent,
                          color: theme.brand,
                          padding: '4px 10px',
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          textDecoration: 'none',
                          fontFamily: theme.font.sans,
                          border: `1px solid ${theme.accentDark}`,
                        }}
                      >
                        Cliente
                      </a>
                      <a
                        href={`/cotizacion/${q.id}/pdf?modo=interno`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="PDF interno (con costo y margen)"
                        style={{
                          background: theme.bg,
                          color: theme.brand,
                          padding: '4px 10px',
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          textDecoration: 'none',
                          fontFamily: theme.font.sans,
                          border: `1px solid ${theme.border}`,
                        }}
                      >
                        Interno
                      </a>
                      <a
                        href={`/cotizacion/${q.id}/fichas`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Fichas técnicas de los productos"
                        style={{
                          background: theme.bg,
                          color: theme.brand,
                          padding: '4px 10px',
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          textDecoration: 'none',
                          fontFamily: theme.font.sans,
                          border: `1px solid ${theme.border}`,
                        }}
                      >
                        Fichas
                      </a>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openQuote && (
        <QuoteDetailModal
          quote={openQuote}
          onClose={() => setOpenQuote(null)}
          onAction={async (action) => {
            const ts = new Date().toISOString();
            const patch: any = action === 'accept'
              ? { accepted_at: ts }                 // el cliente acepta; el equipo marca "Pagada" al recibir el pago
              : { status: 'lost' };                 // rechazada -> Perdida
            await supabase.from('quotes').update(patch).eq('id', openQuote.id);
            setQuotes(prev => prev.map(x => x.id === openQuote.id ? { ...x, ...patch } : x));
            setOpenQuote(null);
          }}
        />
      )}
    </PortalContainer>
  );
}

function QuoteDetailModal({
  quote, onClose, onAction,
}: {
  quote: Quote;
  onClose: () => void;
  onAction: (action: 'accept' | 'reject') => void;
}) {
  const [items, setItems] = useState<QuoteItem[]>([]);
  const { fmtUsd, fmtMxn, usdMxn } = useCurrency();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('quote_items')
      .select('id, sku, description, quantity, unit, unit_price, discount_pct, line_total')
      .eq('quote_id', quote.id)
      .order('sort_order')
      .then(({ data }) => {
        setItems((data as QuoteItem[]) ?? []);
        setLoading(false);
      });
  }, [quote.id]);

  const canDecide = quote.status === 'sent';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 880,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '20px 28px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              Cotización · {quote.kind === 'to_nuled' ? 'mayoreo a NULED' : 'a tu cliente'}
            </div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 24, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>
              {quote.quote_number}
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: theme.textMuted }}>
              <StatusTag status={quote.status} />
              {quote.valid_until && <span style={{ marginLeft: 8 }}>Vence: <strong style={{ color: theme.brand }}>{quote.valid_until}</strong></span>}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', fontSize: 28,
            color: theme.textMuted, cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ padding: 28, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ color: theme.textMuted }}>Cargando ítems…</div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 24 }}>
                <thead>
                  <tr style={{ background: theme.bgSoft }}>
                    <Th>SKU / Descripción</Th>
                    <Th style={{ textAlign: 'right' }}>Cant.</Th>
                    <Th style={{ textAlign: 'right' }}>P. Unit USD</Th>
                    <Th style={{ textAlign: 'right' }}>Desc.</Th>
                    <Th style={{ textAlign: 'right' }}>Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <Td>
                        <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{it.sku}</div>
                        <div style={{ fontSize: 13, color: theme.brand, marginTop: 2 }}>{it.description}</div>
                      </Td>
                      <Td style={{ textAlign: 'right' }}>{it.quantity} <span style={{ color: theme.textMuted, fontSize: 11 }}>{it.unit}</span></Td>
                      <Td style={{ textAlign: 'right' }}>{fmtUsd(it.unit_price)}</Td>
                      <Td style={{ textAlign: 'right', color: theme.textMuted }}>{it.discount_pct > 0 ? `−${it.discount_pct}%` : '—'}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>{fmtUsd(it.line_total)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                <div style={{ flex: 1 }}>
                  {quote.notes && (
                    <div style={{ marginBottom: 16 }}>
                      <Label>Notas</Label>
                      <div style={{ fontSize: 13, color: theme.brand, lineHeight: 1.6 }}>{quote.notes}</div>
                    </div>
                  )}
                  {quote.payment_terms && (
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                      <strong style={{ color: theme.textMuted }}>Pago:</strong>{' '}
                      <span style={{ color: theme.brand }}>{quote.payment_terms}</span>
                    </div>
                  )}
                  {quote.delivery_terms && (
                    <div style={{ fontSize: 12 }}>
                      <strong style={{ color: theme.textMuted }}>Entrega:</strong>{' '}
                      <span style={{ color: theme.brand }}>{quote.delivery_terms}</span>
                    </div>
                  )}
                </div>
                <div style={{ minWidth: 240, padding: 14, background: theme.bgFooter, border: `1px solid ${theme.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${theme.border}` }}>
                    <span style={{ color: theme.textMuted, fontSize: 12 }}>Subtotal</span>
                    <strong style={{ color: theme.brand }}>{fmtUsd(quote.subtotal ?? quote.total)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', borderTop: `2px solid ${theme.border}` }}>
                    <span style={{ color: theme.brand, fontWeight: 700 }}>TOTAL USD</span>
                    <span style={{ fontFamily: theme.font.head, fontSize: 22, color: theme.accentDark, fontWeight: 700 }}>
                      {fmtUsd(quote.total)}
                    </span>
                  </div>
                  {usdMxn && (
                    <div style={{ fontSize: 11, color: theme.textMuted, textAlign: 'right', marginTop: 2 }}>
                      ≈ {fmtMxn(quote.total * usdMxn)} (TC {usdMxn.toFixed(2)})
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '16px 28px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {canDecide && (
            <>
              <button onClick={() => onAction('reject')} style={{
                background: 'transparent', color: theme.err,
                border: `1px solid ${theme.err}`,
                padding: '10px 18px', fontSize: 12, fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: theme.font.sans,
              }}>
                Rechazar
              </button>
              <button onClick={() => onAction('accept')} style={{
                background: theme.accent, color: theme.brand, border: 'none',
                padding: '10px 18px', fontSize: 12, fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: theme.font.sans,
              }}>
                ✓ Aceptar cotización
              </button>
            </>
          )}
          <button onClick={onClose} style={{
            background: theme.brand, color: theme.textOnBrand, border: 'none',
            padding: '10px 18px', fontSize: 12, fontWeight: 700,
            letterSpacing: 0.4, textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: theme.font.sans,
          }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusTag({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    draft:    { color: theme.textMuted, label: 'Borrador' },
    sent:     { color: theme.warn,      label: 'Recibida' },
    viewed:   { color: theme.brandTag.lutron, label: 'Revisando' },
    accepted: { color: theme.accentDark, label: 'Aceptada' },
    rejected: { color: theme.err,       label: 'Rechazada' },
    expired:  { color: theme.err,       label: 'Vencida' },
  };
  const m = map[status] ?? { color: theme.textMuted, label: status };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      padding: '2px 8px',
      color: m.color, border: `1px solid ${m.color}`,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{m.label}</span>
  );
}

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : 'transparent',
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none',
      padding: '12px 20px',
      fontSize: 13,
      fontWeight: 700,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      display: 'flex',
      alignItems: 'center',
    }}>{children}</button>
  );
}

function Banner({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: theme.bgFooter,
      borderLeft: `4px solid ${color}`,
      padding: 14,
      marginBottom: 16,
      fontSize: 13,
      color: theme.textMuted,
      lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '12px 14px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, color: theme.textMuted,
      textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      marginBottom: 6,
    }}>{children}</div>
  );
}
